// components/Common/SearchBar.tsx
import React, { useState, useEffect } from 'react';
import { Box, Typography, InputBase, CircularProgress, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearchServiceGetApiSearch } from '../../api/generated/queries/queries';
import { SearchResultDTO } from '../../api/generated/requests/types.gen';
import TaskDetailDialog from '../Tasks/TaskDetailDialog';
import LeadDetailDialog from '../Leads/LeadDetailDialog';

interface SearchResult {
    id: number;
    type: 'COMMENT' | 'TASK' | 'LEAD' | 'NOTE' | 'FILE';
    data: {
        [key: string]: unknown;
    };
    relevanceScore: number;
    highlightedText: string;
}

const getTypeLabel = (type: string): string => {
    switch (type) {
        case 'TASK':
            return 'ZADANIE';
        case 'LEAD':
            return 'KONTAKT';
        case 'COMMENT':
            return 'KOMENTARZ';
        case 'NOTE':
            return 'NOTATKA';
        case 'FILE':
            return 'PLIK';
        default:
            return type;
    }
};

const getTypeColor = (type: string): { bg: string; text: string } => {
    switch (type) {
        case 'TASK':
            return { bg: '#e3f2fd', text: '#1976d2' };
        case 'LEAD':
            return { bg: '#f3e5f5', text: '#7b1fa2' };
        case 'COMMENT':
            return { bg: '#e8f5e8', text: '#2e7d32' };
        case 'NOTE':
            return { bg: '#fff3e0', text: '#f57c00' };
        case 'FILE':
            return { bg: '#e1f5fe', text: '#0277bd' };
        default:
            return { bg: '#f5f5f5', text: '#666' };
    }
};

const SearchBar: React.FC = () => {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [openTaskDialog, setOpenTaskDialog] = useState(false);
    const [openLeadDialog, setOpenLeadDialog] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
    const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
    const [taskDialogInitialTab, setTaskDialogInitialTab] = useState<number>(0);
    const [leadDialogInitialTab, setLeadDialogInitialTab] = useState<number>(0);
    const [highlightCommentId, setHighlightCommentId] = useState<number | null>(null);
    const navigate = useNavigate();

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300);

        return () => clearTimeout(timer);
    }, [query]);

    // Global search query
    const { data: searchData, isLoading: isSearching } = useGlobalSearchServiceGetApiSearch(
        {
            searchTerm: debouncedQuery,
        },
        ['global-search', debouncedQuery],
        {
            enabled: debouncedQuery.length >= 2,
        }
    );

    // Transform results
    useEffect(() => {
        if (debouncedQuery.length < 2) {
            setResults([]);
            return;
        }

        // Handle the case where searchData might be a single object or array
        const searchResults = Array.isArray(searchData) ? searchData : (searchData ? [searchData] : []);
        
        const transformedResults: SearchResult[] = searchResults.map((result: SearchResultDTO) => ({
            id: result.id!,
            type: result.type!,
            data: result.data!,
            relevanceScore: result.relevanceScore!,
            highlightedText: result.highlightedText!,
        }));

        setResults(transformedResults);
    }, [searchData, debouncedQuery]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
    };

    const handleClearSearch = () => {
        setQuery('');
        setResults([]);
    };

    const handleResultClick = (result: SearchResult) => {
        switch (result.type) {
            case 'TASK':
                setSelectedTaskId(result.id);
                setTaskDialogInitialTab(0); // Default to details tab
                setHighlightCommentId(null);
                setOpenTaskDialog(true);
                break;
            case 'LEAD':
                setSelectedLeadId(result.id);
                setLeadDialogInitialTab(0); // Default to details tab
                setOpenLeadDialog(true);
                break;
            case 'COMMENT':
                // For comments, navigate to the related object if available
                const commentData = result.data as any;
                if (commentData.objectType === 'TASK' && commentData.objectId) {
                    // Open task modal with comments tab and highlight specific comment
                    setSelectedTaskId(commentData.objectId);
                    setTaskDialogInitialTab(1); // Comments tab is at index 1
                    setHighlightCommentId(result.id);
                    setOpenTaskDialog(true);
                    console.log('Comment clicked - opening task modal with comments tab:', result);
                } else if (commentData.objectType === 'LEAD' && commentData.objectId) {
                    // Open lead modal with comments tab and highlight specific comment
                    setSelectedLeadId(commentData.objectId);
                    setLeadDialogInitialTab(1); // Assuming comments tab is at index 1 for leads too
                    setHighlightCommentId(result.id);
                    setOpenLeadDialog(true);
                    console.log('Comment clicked - opening lead modal with comments tab:', result);
                } else {
                    // For standalone comments/notes, navigate to notes page
                    console.log('Comment clicked - navigating to notes page:', result);
                    navigate(`/notes#${result.id}`);
                }
                break;
            case 'NOTE':
                // For notes, navigate to notes page with specific note ID
                console.log('Note clicked:', result);
                navigate(`/notes#${result.id}`);
                break;
            case 'FILE':
                // For files, check status and navigate to appropriate tab
                const fileData = result.data as any;
                const fileStatus = fileData.status;
                console.log('File clicked:', result, 'Status:', fileStatus);
                
                if (fileStatus === 'DETACHED') {
                    // Navigate to trash tab (index 1)
                    navigate(`/files?tab=1#${result.id}`);
                } else {
                    // Navigate to active tab (index 0) - default for ACTIVE or no status
                    navigate(`/files?tab=0#${result.id}`);
                }
                break;
            default:
                console.log('Unknown type:', result.type);
        }
        setQuery('');
        setResults([]);
    };

    const handleCloseTaskDialog = () => {
        setOpenTaskDialog(false);
        setSelectedTaskId(null);
    };

    const handleCloseLeadDialog = () => {
        setOpenLeadDialog(false);
        setSelectedLeadId(null);
    };

    return (
        <Box sx={{ position: 'relative', width: 350, mx: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderRadius: '999px',
                    px: 1.5,
                    py: 0.2,
                    boxShadow: 1,
                    height: 28,
                }}
            >
                <SearchIcon sx={{ color: 'action.active', fontSize: 16, mr: 1 }} />
                <InputBase
                    placeholder="Szukaj klienta, zadania, notatki lub pliki..."
                    fullWidth
                    value={query}
                    onChange={handleInputChange}
                    sx={{ 
                        fontSize: '14px',
                        color: '#333',
                        '& input::placeholder': {
                            color: '#666',
                            opacity: 1
                        }
                    }}
                />
                {query && (
                    <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{ 
                            p: 0.25,
                            ml: 0.5,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        <ClearIcon sx={{ fontSize: 14, color: 'action.active' }} />
                    </IconButton>
                )}
                {isSearching && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
            </Box>

            {(results.length > 0 || (debouncedQuery.length >= 2 && !isSearching)) && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        borderRadius: 2,
                        boxShadow: 3,
                        maxHeight: 300,
                        overflowY: 'auto',
                        zIndex: 1200,
                        mt: 0.5,
                    }}
                >
                    {results.length > 0 ? (
                        results.map((result) => (
                            <Box
                                key={`${result.type}-${result.id}`}
                                onClick={() => handleResultClick(result)}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    '&:hover': { backgroundColor: '#f5f5f5' },
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    '&:last-child': { borderBottom: 'none' },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            backgroundColor: getTypeColor(result.type).bg,
                                            color: getTypeColor(result.type).text,
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 1,
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            mr: 1,
                                        }}
                                    >
                                        {getTypeLabel(result.type)}
                                    </Typography>
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: '#000' }}>
                                    {result.highlightedText}
                                </Typography>
                            </Box>
                        ))
                    ) : (
                        <Box sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                Brak wyników dla "{debouncedQuery}"
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Task Detail Dialog */}
            <TaskDetailDialog
                open={openTaskDialog}
                taskId={selectedTaskId}
                onClose={handleCloseTaskDialog}
                initialTab={taskDialogInitialTab}
                highlightCommentId={highlightCommentId}
            />

            {/* Lead Detail Dialog */}
            <LeadDetailDialog
                open={openLeadDialog}
                leadId={selectedLeadId}
                onClose={handleCloseLeadDialog}
                initialTab={leadDialogInitialTab}
                highlightCommentId={highlightCommentId}
            />
        </Box>
    );
};

export default SearchBar;
